/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent, useCallback } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import styled, { DefaultTheme, useTheme } from 'styled-components/native'

import { useHandleFocus } from 'libs/hooks/useHandleFocus'
import { useHandleHover } from 'libs/hooks/useHandleHover'
import { accessibleCheckboxProps } from 'shared/accessibilityProps/accessibleCheckboxProps'
import { RequiredLabel } from 'ui/components/inputs/RequiredLabel'
import { RequiredIndicator } from 'ui/components/inputs/types'
import { TouchableOpacity } from 'ui/components/TouchableOpacity'
import { getCheckboxColors } from 'ui/designSystem/Checkbox/getCheckboxColors'
import { getCheckboxState } from 'ui/designSystem/Checkbox/getCheckboxState'
import { SelectableAsset } from 'ui/designSystem/SelectableAsset'
import {
  CheckboxState,
  SelectableAssetProps,
  SelectableSizing,
  SelectableVariant,
} from 'ui/designSystem/types'
import { useSpaceBarAction } from 'ui/hooks/useSpaceBarAction'
import { CheckboxMarkChecked } from 'ui/svg/icons/CheckboxMarkChecked'
import { CheckboxMarkIndeterminate } from 'ui/svg/icons/CheckboxMarkIndeterminate'
import { Typo } from 'ui/theme'
import { customFocusOutline } from 'ui/theme/customFocusOutline/customFocusOutline'
import { getHoverStyle } from 'ui/theme/getHoverStyle/getHoverStyle'

const isWeb = Platform.OS === 'web'

type CheckboxBaseCheckedOnly = {
  indeterminate?: boolean
  isChecked: boolean
}

type CheckboxBaseIndeterminateOnly = {
  indeterminate?: boolean
  isChecked: boolean
}

type CheckboxBase = {
  label: string
  onPress: (isChecked: boolean) => void
  required?: boolean
  requiredIndicator?: RequiredIndicator
  hasError?: boolean
  disabled?: boolean
  accessibilityLabel?: string
} & (CheckboxBaseCheckedOnly | CheckboxBaseIndeterminateOnly)

type DefaultCheckbox = CheckboxBase & {
  asset?: never
  collapsed?: never
  description?: never
  sizing?: SelectableSizing
  variant?: 'default'
}

type DetailedCheckbox = CheckboxBase & {
  asset?: SelectableAssetProps
  collapsed?: never
  description?: string
  sizing?: SelectableSizing
  variant?: 'detailed'
}

type DetailedCheckboxWithCollapsed = CheckboxBase & {
  asset?: SelectableAssetProps
  collapsed: React.ReactNode
  description?: string
  sizing?: Extract<SelectableSizing, 'fill'>
  variant?: 'detailed'
}

export type CheckboxProps = DefaultCheckbox | DetailedCheckbox | DetailedCheckboxWithCollapsed

export const Checkbox: FunctionComponent<CheckboxProps> = ({
  asset,
  collapsed,
  description,
  disabled,
  sizing,
  hasError,
  indeterminate = false,
  isChecked,
  label,
  accessibilityLabel,
  onPress,
  required,
  requiredIndicator = 'explicit',
  variant = 'default',
}) => {
  const focusProps = useHandleFocus()
  const hoverProps = useHandleHover()

  const { designSystem, checkbox } = useTheme()
  const { width } = useWindowDimensions()
  const isSmallScreen = width <= 350
  const isTagVariant = asset?.variant === 'tag'
  const isTagStacked = isSmallScreen && isTagVariant

  const onToggle = useCallback(() => {
    onPress(!isChecked)
  }, [isChecked, onPress])

  useSpaceBarAction(focusProps.isFocus ? onToggle : undefined)

  const effectiveSizing: SelectableSizing = sizing ?? (variant === 'detailed' ? 'fill' : 'hug')

  const state = getCheckboxState(isChecked, indeterminate, hasError, disabled)
  const isDisabled = state.includes('disabled')
  const isCheckedState = state.includes('checked')
  const isIndeterminate = state.includes('indeterminate')

  const colorMark = disabled ? designSystem.color.icon.disabled : designSystem.color.icon.inverted
  const checkboxMarkSize = checkbox.size / 1.75 // Parent padding doesn't have effect on CheckboxMarkIndeterminate

  const computedAccessibilityLabel = accessibilityLabel ?? label
  const Label = isCheckedState || isIndeterminate ? StyledBodyAccent : StyledBody

  const renderCheckboxMark = () => {
    if (isIndeterminate) {
      return (
        <CheckboxMarkIndeterminate
          color={colorMark}
          width={checkboxMarkSize}
          height={checkboxMarkSize}
        />
      )
    }
    if (isCheckedState) {
      return (
        <CheckboxMarkChecked color={colorMark} width={checkboxMarkSize} height={checkboxMarkSize} />
      )
    }
    return null
  }

  return (
    <CheckboxContainer
      {...accessibleCheckboxProps({
        checked: isChecked,
        label: computedAccessibilityLabel,
        required,
      })}
      state={state}
      variant={variant}
      sizing={effectiveSizing}
      collapsed={collapsed}
      onPress={onToggle}
      {...focusProps}
      {...hoverProps}>
      <ContentContainer>
        <LeftBox state={state} variant={variant} disabled={isDisabled} {...hoverProps}>
          {renderCheckboxMark()}
        </LeftBox>
        <RightBox>
          <Label state={state} {...hoverProps}>
            {label}
            {required ? <RequiredLabel indicator={requiredIndicator} lowercase /> : null}
          </Label>
          {description ? (
            <StyledBodyAccentXs state={state} {...hoverProps}>
              {description}
            </StyledBodyAccentXs>
          ) : null}
        </RightBox>
        {asset && !isTagStacked ? (
          <BottomBox>
            <SelectableAsset {...asset} disable={isDisabled} />
          </BottomBox>
        ) : null}
      </ContentContainer>
      {asset && isTagStacked ? (
        <AssetStackedContainer>
          <SelectableAsset {...asset} disable={isDisabled} />
        </AssetStackedContainer>
      ) : null}
      {collapsed ? <CollapsedContainer>{collapsed}</CollapsedContainer> : null}
    </CheckboxContainer>
  )
}

type ContainerProps = {
  state: CheckboxState[]
  variant: SelectableVariant
  collapsed?: React.ReactNode
  sizing?: SelectableSizing
  isHover?: boolean
  isFocus?: boolean
}

const getBorderHoverStyle = ({ theme, state, isHover }: LabelHoverStyleParams) => {
  const disabled = state.includes('disabled')
  const error = state.includes('error')
  if (disabled || error) return {}
  return getHoverStyle({ borderColor: theme.designSystem.color.border.brandPrimary, isHover })
}

const CheckboxContainer = styled(TouchableOpacity)<ContainerProps>(({
  theme,
  state,
  variant,
  sizing,
  isFocus,
  isHover,
  collapsed,
}) => {
  const { borderColor, backgroundColor } = getCheckboxColors({
    state,
    variant,
    collapsed: !!collapsed,
    theme,
    componentType: 'container',
  })
  const isDetailed = variant === 'detailed'
  const isDisabled = state.includes('disabled')

  return {
    cursor: isDisabled ? 'default' : 'pointer',
    width: sizing === 'fill' ? '100%' : undefined,
    alignSelf: sizing === 'hug' && isWeb ? 'flex-start' : undefined,
    ...(isDetailed && {
      backgroundColor,
      border: 1,
      borderColor,
      borderRadius: theme.designSystem.size.borderRadius.m,
      padding: theme.designSystem.size.spacing.l,
    }),
    ...customFocusOutline({ theme, isFocus }),
    ...getBorderHoverStyle({ state, theme, isHover }),
  }
})

const ContentContainer = styled.View(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  columnGap: theme.designSystem.size.spacing.m,
}))

type LeftBoxProps = {
  variant: SelectableVariant
  state: CheckboxState[]
  isHover?: boolean
  disabled?: boolean
}

const LeftBox = styled.View<LeftBoxProps>(({ theme, variant, isHover, state }) => {
  const { borderColor, backgroundColor } = getCheckboxColors({
    state,
    variant,
    collapsed: false,
    theme,
    componentType: 'mark',
  })
  return {
    width: theme.checkbox.size,
    height: theme.checkbox.size,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.designSystem.size.borderRadius.s,
    border: theme.checkbox.border.size,
    borderColor,
    backgroundColor,
    ...getBorderHoverStyle({ state, theme, isHover }),
  }
})

type LabelColorParams = {
  theme: DefaultTheme
  state: CheckboxState[]
}

const getLabelColor = ({ state, theme }: LabelColorParams) => {
  const isDisabled = state.includes('disabled')
  return isDisabled ? theme.designSystem.color.text.disabled : theme.designSystem.color.text.default
}

type LabelHoverStyleParams = LabelColorParams & {
  isHover?: boolean
}

const getTextHoverStyle = ({ theme, state, isHover }: LabelHoverStyleParams) => {
  const isDisabled = state.includes('disabled')
  const error = state.includes('error')
  if (isDisabled || error) return {}
  return getHoverStyle({ textColor: theme.designSystem.color.text.brandPrimary, isHover })
}

const StyledBody = styled(Typo.Body)<{ state: CheckboxState[]; isHover?: boolean }>(
  ({ state, theme, isHover }) => ({
    color: getLabelColor({ state, theme }),
    ...getTextHoverStyle({ state, theme, isHover }),
  })
)

const StyledBodyAccent = styled(Typo.BodyAccent)<{ state: CheckboxState[]; isHover?: boolean }>(
  ({ state, theme, isHover }) => ({
    color: getLabelColor({ state, theme }),
    ...getTextHoverStyle({ state, theme, isHover }),
  })
)

const StyledBodyAccentXs = styled(Typo.BodyAccentXs)<{
  state: CheckboxState[]
  isHover?: boolean
}>(({ state, isHover, theme }) => {
  const isDisable = state.includes('disabled')
  return {
    color: isDisable
      ? theme.designSystem.color.text.disabled
      : theme.designSystem.color.text.default,
    ...getTextHoverStyle({ state, theme, isHover }),
  }
})

const RightBox = styled.View({
  flex: 1,
})

const BottomBox = styled.View({
  justifyContent: 'center',
})

const CollapsedContainer = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.l,
}))

const AssetStackedContainer = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xs,
  marginLeft: theme.checkbox.size + theme.designSystem.size.spacing.m,
}))
